"use client";

import { useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, ArrowUpRight, TrendingDown, TrendingUp } from "@/components/icons";
import { cn } from "@/lib/utils";
import { SectionHeader } from "@/components/ui";
import {
  PricingSlider,
  sliderThumbStyles,
} from "@/components/sections/products/shared/pricing-slider";
import { buildGbTiers } from "@/lib/pricing-utils";
import { useProductsInfo } from "@/lib/products-info";
import { REGISTER_URL, ROUTES } from "@/lib/constants";
import {
  SOAX_PRICING_URL,
  SOAX_VAT_NOTE,
  SOAX_TIER_NOTE,
  SOAX_TIER1_PLANS,
  SOAX_TIER2_PLANS,
  PRICING_VERIFIED_DATE,
  PROXIDIZE_MOBILE_FALLBACK_TIERS,
  PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS,
  soaxMonthlyCost,
  type ProxyProduct,
} from "./pricing-data";

const MotionDiv = motion.div;

const currency = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  });

const PRODUCT_META: Record<ProxyProduct, { label: string; pricingRoute: string }> = {
  mobile: { label: "Mobile proxies", pricingRoute: ROUTES.pricingMobileProxies },
  residential: { label: "Residential proxies", pricingRoute: ROUTES.pricingResidentialProxies },
};

type SoaxTier = "tier1" | "tier2";

const SOAX_TIER_META: Record<SoaxTier, { label: string; plans: typeof SOAX_TIER1_PLANS }> = {
  tier1: { label: "Tier 1 — US, UK, Canada, Germany", plans: SOAX_TIER1_PLANS },
  tier2: { label: "Tier 2 — India", plans: SOAX_TIER2_PLANS },
};

export default function SoaxCostCalculator() {
  const prefersReducedMotion = useReducedMotion();
  const posthog = usePostHog();
  const products = useProductsInfo();
  const hasTracked = useRef(false);

  const [product, setProduct] = useState<ProxyProduct>("mobile");
  const [soaxTier, setSoaxTier] = useState<SoaxTier>("tier1");

  // Live Proxidize prices when the pricing API responds; honest static
  // fallbacks (the same ones the pricing pages use) when it doesn't.
  const mobileTiers = useMemo(
    () => buildGbTiers(products?.["Monthly Per GB Mobile"] ?? [], PROXIDIZE_MOBILE_FALLBACK_TIERS),
    [products]
  );
  const residentialTiers = useMemo(
    () =>
      buildGbTiers(
        products?.["Monthly Per GB Residential"] ?? [],
        PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS
      ),
    [products]
  );

  const tiers = product === "mobile" ? mobileTiers : residentialTiers;
  const steps = useMemo(() => tiers.map((t) => t.gb), [tiers]);

  const [gb, setGb] = useState(100);
  const selected =
    tiers.find((t) => t.gb === gb) ??
    tiers.reduce((best, t) => (Math.abs(t.gb - gb) < Math.abs(best.gb - gb) ? t : best), tiers[0]);
  const activeGb = selected.gb;

  // SOAX prices mobile and residential at the same rate, so this doesn't
  // depend on `product` — only the selected country tier and Proxidize's
  // side of the comparison change.
  const soax = soaxMonthlyCost(activeGb, SOAX_TIER_META[soaxTier].plans);
  const proxidizeCost = selected.totalPrice;
  const savings = soax.cost - proxidizeCost;
  const savingsPct = Math.round((Math.abs(savings) / soax.cost) * 100);
  const meta = PRODUCT_META[product];

  function track() {
    if (!hasTracked.current) {
      hasTracked.current = true;
      posthog?.capture("alternatives_calculator_used", { competitor: "soax" });
    }
  }

  function handleChange(value: number) {
    setGb(value);
    track();
  }

  function handleProductSwitch(next: ProxyProduct) {
    setProduct(next);
    track();
    posthog?.capture("alternatives_calculator_product_switched", {
      competitor: "soax",
      product: next,
    });
  }

  function handleTierSwitch(next: SoaxTier) {
    setSoaxTier(next);
    track();
    posthog?.capture("alternatives_calculator_soax_tier_switched", {
      competitor: "soax",
      tier: next,
    });
  }

  return (
    <section
      id="cost-calculator"
      className="py-section border-border scroll-mt-24 border-b"
      aria-labelledby="calculator-heading"
    >
      <style>{sliderThumbStyles}</style>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55 }}
          className="mb-14 max-w-2xl"
        >
          <SectionHeader
            eyebrow="Cost Calculator"
            id="calculator-heading"
            heading="What would your monthly proxy bill be?"
            body="Pick a product, drag the slider to your monthly data usage, and pick SOAX's country tier. Proxidize prices come from our live pricing. SOAX prices mobile and residential at the same committed-spend rate, but that rate depends on the country — this affects only the SOAX side of the comparison."
          />
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="bg-card card-wash card-hover border-border relative overflow-hidden rounded-2xl border p-6 sm:p-8"
        >
          <div className="relative mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-muted-foreground text-sm">Monthly data usage</p>
              <p className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
                {activeGb.toLocaleString("en-US")} GB
              </p>
            </div>

            <div
              role="group"
              aria-label="Proxy product"
              className="border-border inline-flex rounded-lg border p-1"
            >
              {(Object.keys(PRODUCT_META) as ProxyProduct[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={product === key}
                  onClick={() => handleProductSwitch(key)}
                  className={cn(
                    "cursor-pointer rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                    product === key
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {PRODUCT_META[key].label}
                </button>
              ))}
            </div>
          </div>

          <PricingSlider
            min={steps[0]}
            max={steps[steps.length - 1]}
            value={activeGb}
            onChange={handleChange}
            steps={steps}
            aria-label={`Monthly ${meta.label.toLowerCase()} data usage in gigabytes`}
          />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-muted-foreground text-xs font-medium">SOAX country tier</span>
            <div
              role="group"
              aria-label="SOAX country tier"
              className="border-border inline-flex rounded-lg border p-1"
            >
              {(Object.keys(SOAX_TIER_META) as SoaxTier[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={soaxTier === key}
                  onClick={() => handleTierSwitch(key)}
                  className={cn(
                    "cursor-pointer rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    soaxTier === key
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {SOAX_TIER_META[key].label}
                </button>
              ))}
            </div>
          </div>

          <div className="relative mt-10 grid gap-5 sm:grid-cols-2">
            <div
              className="rounded-xl border p-6"
              style={{
                borderColor: "color-mix(in srgb, var(--brand-green) 35%, transparent)",
                backgroundColor: "color-mix(in srgb, var(--brand-green) 5%, transparent)",
              }}
            >
              <p style={{ color: "var(--brand-green)" }} className="text-sm font-semibold">
                Proxidize
              </p>
              <p
                className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--brand-green)" }}
              >
                {currency(proxidizeCost)}
                <span className="text-muted-foreground text-base font-normal">/mo</span>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {currency(selected.monthlyPPG)}/GB · no minimum
              </p>
            </div>

            <div className="border-border bg-background/40 rounded-xl border p-6">
              <p className="text-foreground text-sm font-semibold">SOAX</p>
              <p className="text-foreground mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                {currency(soax.cost)}
                <span className="text-muted-foreground text-base font-normal">/mo</span>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">{soax.planLabel}</p>
            </div>
          </div>

          <div aria-live="polite">
            {savings > 0 ? (
              <p className="text-foreground mt-6 flex items-center gap-2 text-sm font-medium">
                <TrendingDown
                  size={16}
                  style={{ color: "var(--brand-green)" }}
                  aria-hidden="true"
                />
                You&rsquo;d save {currency(savings)}/month ({savingsPct}%) with Proxidize at this
                volume.
              </p>
            ) : savings < 0 ? (
              <p className="text-muted-foreground mt-6 flex items-center gap-2 text-sm font-medium">
                <TrendingUp size={16} aria-hidden="true" />
                At this volume, SOAX&rsquo;s committed-spend tier is {currency(-savings)}/month
                cheaper ({savingsPct}%) than Proxidize&rsquo;s flat rate.
              </p>
            ) : (
              <p className="text-muted-foreground mt-6 text-sm font-medium">
                Proxidize and SOAX cost the same at this volume.
              </p>
            )}
          </div>

          <p className="text-muted-foreground mt-4 text-xs leading-relaxed">
            SOAX prices both products on the same tiered ladder: a flat rate per plan (Sandbox $
            {SOAX_TIER_META[soaxTier].plans[0].perGb.toFixed(2)}/GB down to Enterprise $
            {SOAX_TIER_META[soaxTier].plans[
              SOAX_TIER_META[soaxTier].plans.length - 1
            ].perGb.toFixed(2)}
            /GB at SOAX&rsquo;s {SOAX_TIER_META[soaxTier].label} rate), each with its own monthly
            minimum commitment from $0 to $3,000 — we show the cheapest plan that fits your volume
            at the selected tier. SOAX pricing verified {PRICING_VERIFIED_DATE};{" "}
            <a
              href={SOAX_PRICING_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-foreground underline underline-offset-2 transition-colors"
            >
              check their live pricing
            </a>
            . {SOAX_VAT_NOTE} {SOAX_TIER_NOTE}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="cta-glow-btn-wrap">
              <a
                href={REGISTER_URL}
                onClick={() =>
                  posthog?.capture("alternatives_cta_clicked", {
                    competitor: "soax",
                    location: "calculator",
                    product,
                    gb: activeGb,
                  })
                }
                className="cta-glow-btn-inner"
              >
                Try Proxidize NOW
                <ArrowRight size={16} className="ml-2" aria-hidden="true" />
              </a>
            </span>
            <Link
              href={meta.pricingRoute}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              Full Proxidize {product} pricing
              <ArrowUpRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.12 }}
          className="border-border bg-card mt-5 rounded-xl border p-6"
        >
          <p className="text-foreground text-sm font-semibold">
            Need real mobile IPs of your own instead of a shared pool?
          </p>
          <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
            Proxidize also sells per-proxy mobile plans, real mobile IPs assigned to your account
            with unlimited bandwidth, per-proxy rotation control, and city/ISP selection, from{" "}
            <strong className="text-foreground font-semibold">$59/proxy/month</strong>. SOAX
            doesn&rsquo;t offer a per-proxy mobile option — its mobile network is bandwidth-only,
            shared-pool access on the same tiered plans above.{" "}
            <Link
              href={`${ROUTES.productMobileProxies}#mobile-product-models`}
              className="text-brand hover:text-brand/80 font-semibold transition-colors"
            >
              See per-proxy plans →
            </Link>
          </p>
        </MotionDiv>
      </div>
    </section>
  );
}
